import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CreditCard, Building2, CheckCircle, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppLayout from '@/components/app/AppLayout';
import StepIndicator from '@/components/app/StepIndicator';
import ServiceCard from '@/components/app/ServiceCard';
import OrderSummary from '@/components/app/OrderSummary';
import InformationStep from '@/components/app/InformationStep';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Services data
const SERVICES = [
  { 
    id: 'llc_formation', 
    name: 'LLC Formation', 
    description: 'Form a Limited Liability Company in your chosen state',
    fee: 199, 
    hasStateFee: true,
    isFormation: true
  },
  { 
    id: 'c_corp_formation', 
    name: 'C-Corp Formation', 
    description: 'Incorporate as a C-Corporation for investors and equity',
    fee: 299, 
    hasStateFee: true,
    isFormation: true
  },
  { 
    id: 's_corp_formation', 
    name: 'S-Corp Formation', 
    description: 'Form an S-Corporation for tax benefits',
    fee: 149, 
    hasStateFee: true,
    isFormation: true
  },
  { 
    id: 'ein_application', 
    name: 'EIN (Tax ID)', 
    description: 'Obtain your Federal Employer Identification Number',
    fee: 99, 
    hasStateFee: false 
  },
  { 
    id: 'registered_agent', 
    name: 'Registered Agent', 
    description: 'Compliance representation in your formation state',
    fee: 150, 
    hasStateFee: false,
    recurring: 'year'
  },
  { 
    id: 'mail_forwarding', 
    name: 'Mail Forwarding', 
    description: 'Professional US business address with mail scanning',
    fee: 200, 
    hasStateFee: false,
    recurring: 'year'
  },
  { 
    id: 'amendment', 
    name: 'State Filings & Amendments', 
    description: 'Update company details, name changes, and more',
    fee: 200, 
    hasStateFee: true,
    isAmendment: true
  },
];

// Add-on services for formation packages
const ADD_ONS = [
  {
    id: 'ein_application',
    name: 'EIN (Tax ID)',
    description: 'Required by IRS to open bank accounts and file taxes',
    fee: 99,
    recommended: true
  },
  {
    id: 'registered_agent',
    name: 'Registered Agent',
    description: 'Required by law in all 50 US states',
    fee: 150,
    recurring: '/yr',
    recommended: true
  }
];

// Amendment sub-types
const AMENDMENT_SUBTYPES = [
  { value: 'name_change', label: 'Name Change' },
  { value: 'address_change', label: 'Address Change' },
  { value: 'registered_agent_change', label: 'Registered Agent Change' },
  { value: 'director_member_change', label: 'Director/Member Change' },
  { value: 'share_structure_change', label: 'Share Structure Change' },
  { value: 'certificate_good_standing', label: 'Certificate of Good Standing' },
  { value: 'franchise_tax_filing', label: 'Franchise Tax Filing' },
  { value: 'annual_report', label: 'Annual Report' },
  { value: 'foreign_qualification', label: 'Foreign Qualification' },
  { value: 'dissolution', label: 'Dissolution' },
];

// Entity type mapping for API calls
const ENTITY_TYPE_MAP = {
  'us_llc': 'llc',
  'us_c_corp': 'c_corp',
  'us_s_corp': 's_corp',
  'us_sole_proprietorship': null
};

// Document requirements per service type (hardcoded - no DB fetch needed)
const SERVICE_DOCUMENT_REQUIREMENTS = {
  llc_formation: [
    { doc_key: 'passport', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'utility_bill', name: 'Utility Bill / Bank Statement', description: 'Proof of address - last 3 months', required: true }
  ],
  c_corp_formation: [
    { doc_key: 'passport', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'utility_bill', name: 'Utility Bill / Bank Statement', description: 'Proof of address - last 3 months', required: true }
  ],
  s_corp_formation: [
    { doc_key: 'passport', name: 'Passport Copy', description: 'Clear color copy of passport bio page', required: true },
    { doc_key: 'utility_bill', name: 'Utility Bill / Bank Statement', description: 'Proof of address - last 3 months', required: true }
  ],
  amendment: [
    { doc_key: 'formation_certificate', name: 'Certificate of Formation', description: 'Your existing company formation certificate', required: true },
    { doc_key: 'ein_letter', name: 'EIN Confirmation Letter', description: 'IRS EIN assignment letter (if applicable)', required: false }
  ],
  ein_application: [],
  registered_agent: [],
  mail_forwarding: []
};

const NewOrderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  
  // Step state
  const [currentStep, setCurrentStep] = useState(1);
  
  // Selection state
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState(['ein_application', 'registered_agent']); // Pre-checked
  const [amendmentSubType, setAmendmentSubType] = useState('');
  
  // Data state
  const [companies, setCompanies] = useState([]);
  const [product, setProduct] = useState(null);
  
  // Pricing state
  const [ourFee, setOurFee] = useState(0);
  const [stateFee, setStateFee] = useState(0);
  const [total, setTotal] = useState(0);
  const [amendmentStateFee, setAmendmentStateFee] = useState(null);
  const [fetchingAmendmentFee, setFetchingAmendmentFee] = useState(false);

  // Partner discount (from authenticated user's household primary)
  const [discountPercentage, setDiscountPercentage] = useState(0);
  
  // Step 2.5 state - Information & Documents
  const [collectedInformation, setCollectedInformation] = useState({});
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [showDetailsStep, setShowDetailsStep] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [bankTransferConfirmed, setBankTransferConfirmed] = useState(false);
  const [createdOrderIds, setCreatedOrderIds] = useState([]);
  const isSubmittingRef = useRef(false); // Ref-based guard for double submission

  // Check for query params on mount
  useEffect(() => {
    const serviceParam = searchParams.get('service');
    const companyParam = searchParams.get('company');
    
    if (serviceParam) {
      const service = SERVICES.find(s => s.id === serviceParam);
      if (service) {
        setSelectedService(service);
        // If company param also present, skip to step 2
        if (companyParam) {
          setCurrentStep(2);
        }
      }
    }
    
    // Fetch companies
    fetchCompanies(companyParam);

    // Fetch current user's partner discount (household primary's discount_percentage)
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setDiscountPercentage(parseInt(res.data?.discount_percentage || 0, 10)))
      .catch(() => setDiscountPercentage(0));
  }, [searchParams, token]);

  // Fetch companies
  const fetchCompanies = async (preSelectId = null) => {
    try {
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const companiesList = response.data.companies || [];
      setCompanies(companiesList);
      
      // Pre-select company if param provided
      if (preSelectId) {
        const company = companiesList.find(c => c.id === preSelectId);
        if (company) {
          setSelectedCompany(company);
        }
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  // Fetch product and pricing when company is selected
  useEffect(() => {
    if (selectedService && selectedCompany && token) {
      fetchProductAndPricing();
    }
  }, [selectedService, selectedCompany, token]);

  // Fetch amendment state fee when sub-type is selected
  useEffect(() => {
    if (selectedService?.isAmendment && selectedCompany && amendmentSubType && token) {
      fetchAmendmentStateFee();
    }
  }, [amendmentSubType, selectedCompany, token]);

  const fetchAmendmentStateFee = async () => {
    if (!selectedCompany || !amendmentSubType) return;
    
    const entityType = ENTITY_TYPE_MAP[selectedCompany.entity_type];
    if (!entityType) {
      setAmendmentStateFee(null);
      return;
    }
    
    setFetchingAmendmentFee(true);
    try {
      const response = await axios.get(`${API}/state-fees/lookup`, {
        params: {
          service_type: 'amendment',
          sub_type: amendmentSubType,
          entity_type: entityType,
          state: selectedCompany.state_of_formation
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fees = response.data.state_fees || [];
      if (fees.length > 0) {
        const fee = fees[0].govt_fee || 0;
        setAmendmentStateFee(fee);
        setStateFee(fee);
        setTotal(ourFee + fee);
      } else {
        setAmendmentStateFee(0);
        setStateFee(0);
        setTotal(ourFee);
      }
    } catch (err) {
      console.error('Error fetching amendment state fee:', err);
      setAmendmentStateFee(null);
    } finally {
      setFetchingAmendmentFee(false);
    }
  };

  const fetchProductAndPricing = async () => {
    if (!selectedService || !selectedCompany) return;
    
    setFetchingPrice(true);
    setError('');

    // Resolve the live, admin-managed product (price / info fields / documents)
    let liveProduct = null;
    let baseFee = selectedService.fee;
    try {
      const prodRes = await axios.get(`${API}/products`, {
        params: { service_type: selectedService.id, state: selectedCompany.state_of_formation, is_active: true, limit: 10 },
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = prodRes.data.products || [];
      liveProduct = list.find(p => !(p.applicable_entity_types?.length) || p.applicable_entity_types.includes(selectedCompany.entity_type)) || list[0] || null;
      if (liveProduct && liveProduct.platform_fees != null) baseFee = liveProduct.platform_fees;
    } catch (e) {
      // fall back to hardcoded service fee
    }
    setProduct(liveProduct);
    setOurFee(baseFee);

    const resolveDetailsStep = () => {
      const liveDocs = liveProduct?.document_requirements || [];
      const liveInfo = liveProduct?.information_fields || [];
      const docRequirements = liveDocs.length > 0 ? liveDocs : (SERVICE_DOCUMENT_REQUIREMENTS[selectedService?.id] || []);
      setShowDetailsStep(docRequirements.length > 0 || liveInfo.length > 0);
    };

    try {
      // Only fetch state fee if service has state fee
      if (selectedService.hasStateFee) {
        const state = selectedCompany.state_of_formation;
        const response = await axios.get(`${API}/state-fees/lookup`, {
          params: {
            service_type: selectedService.id,
            state: state,
            entity_type: selectedCompany.entity_type?.replace('us_', '')
          },
          headers: { Authorization: `Bearer ${token}` }
        });
        const fees = response.data.state_fees || [];
        const govtFee = fees.length > 0 ? (fees[0].govt_fee || 0) : 0;
        setStateFee(govtFee);
        setTotal(baseFee + govtFee);
      } else {
        setStateFee(0);
        setTotal(baseFee);
      }
      resolveDetailsStep();
    } catch (err) {
      console.error('Error fetching state fee:', err);
      setStateFee(0);
      setTotal(baseFee);
      resolveDetailsStep();
    } finally {
      setFetchingPrice(false);
    }
  };

  // ---- Partner discount pricing helpers ----
  // Discount applies ONLY to service-side fees (ourFee + add-ons). State fee never discounted.
  const serviceSubtotal = () => {
    let s = ourFee || 0;
    if (selectedService?.isFormation) {
      selectedAddOns.forEach(id => {
        const a = ADD_ONS.find(x => x.id === id);
        if (a) s += (a.fee || 0);
      });
    }
    return s;
  };

  const discountAmount = () => {
    if (!discountPercentage || discountPercentage <= 0) return 0;
    return Math.round(serviceSubtotal() * discountPercentage) / 100;
  };

  // Calculate total including add-ons (now applies partner discount)
  const calculateTotalWithAddOns = () => {
    const service = serviceSubtotal();
    const discounted = service - discountAmount();
    return Math.round((discounted + (stateFee || 0)) * 100) / 100;
  };

  // Handle service selection
  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setProduct(null);
    setStateFee(0);
    setShowDetailsStep(false);
    setCollectedInformation({});
    setUploadedDocuments({});
    setAmendmentSubType('');
    setAmendmentStateFee(null);
    // Reset add-ons to default for formation services
    if (service.isFormation) {
      setSelectedAddOns(['ein_application', 'registered_agent']);
    } else {
      setSelectedAddOns([]);
    }
  };

  // Handle add-on toggle
  const toggleAddOn = (addOnId) => {
    setSelectedAddOns(prev => 
      prev.includes(addOnId) 
        ? prev.filter(id => id !== addOnId)
        : [...prev, addOnId]
    );
  };

  // Handle company selection
  const handleCompanySelect = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    setSelectedCompany(company);
    setAmendmentSubType('');
    setAmendmentStateFee(null);
  };

  // Handle step navigation
  const handleNext = () => {
    if (currentStep === 1 && !selectedService) {
      setError('Please select a service');
      return;
    }
    if (currentStep === 2 && !selectedCompany) {
      setError('Please select a company');
      return;
    }
    if (currentStep === 2 && selectedService?.isAmendment && !amendmentSubType) {
      setError('Please select an amendment type');
      return;
    }
    setError('');
    
    // Determine max step based on whether details step is shown
    const maxStep = showDetailsStep ? 4 : 3;
    
    // If on step 2 and no details step needed, skip to payment (step 3)
    if (currentStep === 2 && !showDetailsStep) {
      setCurrentStep(3);
    } else {
      setCurrentStep(prev => Math.min(prev + 1, maxStep));
    }
  };

  const handleBack = () => {
    setError('');
    
    // If on step 3 (payment) and no details step, go back to step 2
    if (currentStep === 3 && !showDetailsStep) {
      setCurrentStep(2);
    } else if (currentStep === 4) {
      // From payment (step 4 with details) go back to details (step 3)
      setCurrentStep(3);
    } else {
      setCurrentStep(prev => Math.max(prev - 1, 1));
    }
  };
  
  // Check if Step 2.5 (Details) validation passes
  const isDetailsStepValid = () => {
    // Prefer the live product's document requirements; fall back to hardcoded
    const docRequirements = (product?.document_requirements?.length ? product.document_requirements : SERVICE_DOCUMENT_REQUIREMENTS[selectedService?.id]) || [];
    
    // If no document requirements, always valid
    if (docRequirements.length === 0) return true;
    
    // Check all required documents are uploaded
    for (const doc of docRequirements) {
      if (doc.required && !uploadedDocuments[doc.doc_key]) {
        return false;
      }
    }
    
    return true;
  };
  
  // Get the actual payment step number
  const getPaymentStep = () => showDetailsStep ? 4 : 3;

  // Helper to save uploaded documents after order creation
  const saveDocumentSubmissions = async (orderId) => {
    const docEntries = Object.values(uploadedDocuments);
    for (const doc of docEntries) {
      try {
        await axios.post(
          `${API}/orders/${orderId}/submissions`,
          {
            document_type: doc.doc_key,
            file_name: doc.file_name,
            file_url: doc.file_url,
            s3_key: doc.s3_key,
            file_size: doc.file_size,
            source: doc.source || 'upload',
            source_document_id: doc.source_document_id || null
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      } catch (err) {
        console.error(`Failed to save document submission for ${doc.doc_key}:`, err);
      }
    }
  };

  // Create all orders (main service + add-ons).
  // Returns null if ANY add-on creation fails — the caller must abort checkout.
  // Setting `setError(...)` surfaces a visible banner in the UI.
  const createAllOrders = async () => {
    const orderIds = [];

    // Create main service order using service_type (no product_id dependency)
    // Path 1 fix: include fee fields in payload
    const isFeeBearer = selectedService?.hasStateFee === true;

    // Block submission if fee-bearing service has no state fee
    if (isFeeBearer && (!stateFee || stateFee <= 0)) {
      const stateName = selectedCompany?.state_of_formation || 'the selected state';
      setError(`We couldn't retrieve the state fee for ${stateName}. Please refresh and try again, or contact support.`);
      setLoading(false);
      return null;
    }

    const mainOrderPayload = {
      company_id: selectedCompany.id,
      service_type: selectedService.id,
      govt_fee: isFeeBearer ? stateFee : 0,
      platform_fee: ourFee,
      total_amount: total,
      collected_information: {
        ...collectedInformation,
        ...(amendmentSubType ? { amendment_type: amendmentSubType } : {})
      }
    };

    const mainOrderResponse = await axios.post(`${API}/orders`, mainOrderPayload, {
      headers: { Authorization: `Bearer ${token}` }
    });
    orderIds.push(mainOrderResponse.data.id);

    // Save document submissions for main order
    if (Object.keys(uploadedDocuments).length > 0) {
      await saveDocumentSubmissions(mainOrderResponse.data.id);
    }

    // Create add-on orders for formation services.
    // CRITICAL: if any add-on fails, abort the entire checkout. We must not
    // proceed to Stripe with a partial order set — the client would otherwise
    // pay only for the main service and lose the add-ons silently.
    if (selectedService?.isFormation && selectedAddOns.length > 0) {
      for (const addOnId of selectedAddOns) {
        try {
          const addOnOrderPayload = {
            company_id: selectedCompany.id,
            service_type: addOnId,
            collected_information: {}
          };

          const addOnOrderResponse = await axios.post(`${API}/orders`, addOnOrderPayload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          orderIds.push(addOnOrderResponse.data.id);
        } catch (err) {
          console.error(`Failed to create add-on order for ${addOnId}:`, err);
          const detail = err.response?.data?.detail || 'Please try again or contact support.';
          setError(`Failed to add ${addOnId.replace(/_/g, ' ')} to your order. ${detail}`);
          return null;
        }
      }
    }

    return orderIds;
  };

  // Handle Stripe payment
  const handleStripePayment = async () => {
    if (!selectedService || !selectedCompany) return;
    if (loading || isSubmittingRef.current) return; // Prevent double submission

    isSubmittingRef.current = true;
    setLoading(true);
    setError('');

    try {
      const orderIds = await createAllOrders();
      if (!orderIds) {
        // createAllOrders already set an error banner — abort.
        isSubmittingRef.current = false;
        return;
      }
      setCreatedOrderIds(orderIds);

      if (orderIds.length === 0) {
        throw new Error('No orders created');
      }

      // Multi-product checkout: pass ALL order ids so the Stripe session
      // charges the full cart and ALL orders flip on payment.
      const paymentResponse = await axios.post(`${API}/payments/stripe/checkout`, {
        order_ids: orderIds,
        origin_url: window.location.origin,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe checkout
      if (paymentResponse.data.checkout_url) {
        window.location.href = paymentResponse.data.checkout_url;
      } else {
        setError('Failed to initiate payment. Please try again or use bank transfer.');
        isSubmittingRef.current = false;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to process payment. Please try again.';
      setError(errorMessage);
      isSubmittingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // Handle bank transfer
  const handleBankTransfer = async () => {
    if (!selectedService || !selectedCompany) return;
    if (loading || isSubmittingRef.current) return; // Prevent double submission
    
    isSubmittingRef.current = true;
    setLoading(true);
    setError('');
    
    try {
      const orderIds = await createAllOrders();
      if (!orderIds) {
        // createAllOrders already set an error banner — abort.
        isSubmittingRef.current = false;
        return;
      }
      setCreatedOrderIds(orderIds);
      setOrderId(orderIds[0]);
      setOrderSuccess(true);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to create order. Please try again.';
      setError(errorMessage);
      isSubmittingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  // Handle "I Have Paid" confirmation
  const handlePaymentConfirmed = async () => {
    setLoading(true);
    try {
      // Update all created orders to payment_pending
      for (const oid of createdOrderIds) {
        await axios.put(`${API}/orders/${oid}/status`, 
          { status: 'payment_pending' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setBankTransferConfirmed(true);
    } catch (err) {
      console.error('Failed to update order status:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check for Stripe success return
  useEffect(() => {
    const success = searchParams.get('success');
    const returnOrderId = searchParams.get('order_id');
    
    if (success === 'true' && returnOrderId) {
      setOrderId(returnOrderId);
      setOrderSuccess(true);
    }
  }, [searchParams]);

  // Success screen
  if (orderSuccess) {
    return (
      <AppLayout>
        <div className="max-w-lg mx-auto text-center py-12">
          <div className="w-20 h-20 bg-[#059669] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-gray-900" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            {bankTransferConfirmed ? 'Thank You!' : 'Order Placed Successfully!'}
          </h1>
          <p className="text-gray-400 mb-2">
            {bankTransferConfirmed 
              ? 'Our team will verify your payment and confirm your order within 1-2 business days.'
              : 'Your order has been submitted and is being processed.'
            }
          </p>
          {orderId && !bankTransferConfirmed && (
            <p className="text-gray-500 text-sm mb-8">
              Order ID: <span className="text-white font-mono">{orderId}</span>
            </p>
          )}
          
          {paymentMethod === 'bank' && !bankTransferConfirmed && (
            <div className="bg-[#111827] border border-gray-800 rounded-lg p-6 text-left mb-8">
              <h3 className="text-white font-semibold mb-4">Payment Instructions</h3>
              <p className="text-gray-400 text-sm mb-4">
                Our team will contact you with payment instructions within 24 hours. 
                You can also email us at <span className="text-[#059669]">hi@starteaseai.com</span> with your Order ID.
              </p>
              <div className="border-t border-gray-700 pt-4 mt-4">
                <p className="text-gray-400 text-sm mb-4">
                  Already made the transfer? Let us know so we can verify your payment faster.
                </p>
                <button
                  onClick={handlePaymentConfirmed}
                  disabled={loading}
                  className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="confirm-payment-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      I Have Paid
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          
          <Link
            to="/dashboard/orders"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white"
            style={{ backgroundColor: '#059669' }}
            data-testid="view-orders-btn"
          >
            View My Orders
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Back Link */}
      <Link 
        to="/dashboard/orders" 
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Orders
      </Link>

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">New Order</h1>
        <p className="text-gray-400 text-sm mt-1">Select a service and place your order</p>
      </div>

      {/* Step Indicator */}
      <StepIndicator 
        currentStep={currentStep} 
        totalSteps={showDetailsStep ? 4 : 3} 
        showDetailsStep={showDetailsStep}
      />

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-6">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Step 1: Select Service */}
      {currentStep === 1 && (
        <div>
          <h2 className="text-lg font-semibold text-white mb-6">Select a Service</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {SERVICES.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                isSelected={selectedService?.id === service.id}
                onClick={() => handleServiceSelect(service)}
              />
            ))}
          </div>

          {/* Add-ons section for formation services */}
          {selectedService?.isFormation && (
            <div className="mt-8 p-6 bg-[#111827] border border-gray-800 rounded-lg">
              <h3 className="text-white font-semibold mb-2">Recommended Add-ons</h3>
              <p className="text-gray-400 text-sm mb-4">These services are essential for your new company:</p>
              
              <div className="space-y-3">
                {ADD_ONS.map((addOn) => (
                  <button
                    key={addOn.id}
                    onClick={() => toggleAddOn(addOn.id)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      selectedAddOns.includes(addOn.id)
                        ? 'border-[#059669] bg-[#059669]/10'
                        : 'border-gray-700 bg-[#0b1310] hover:border-gray-600'
                    }`}
                    data-testid={`addon-${addOn.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedAddOns.includes(addOn.id)
                            ? 'border-[#059669] bg-[#059669]'
                            : 'border-gray-600'
                        }`}>
                          {selectedAddOns.includes(addOn.id) && (
                            <Check className="w-3 h-3 text-black" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">{addOn.name}</p>
                          <p className="text-gray-500 text-sm">{addOn.description}</p>
                        </div>
                      </div>
                      <span className="text-[#059669] font-semibold">
                        ${addOn.fee}{addOn.recurring || ''}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-700 flex justify-between items-center">
                <span className="text-gray-400">Estimated Total:</span>
                <span className="text-[#059669] text-xl font-bold">
                  ${selectedService.fee + selectedAddOns.reduce((sum, id) => {
                    const addOn = ADD_ONS.find(a => a.id === id);
                    return sum + (addOn?.fee || 0);
                  }, 0)} + state fee
                </span>
              </div>
            </div>
          )}
          
          <div className="flex justify-end mt-6">
            <button
              onClick={handleNext}
              disabled={!selectedService}
              className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#059669' }}
              data-testid="step1-next-btn"
            >
              Next
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Select Company + Review Price */}
      {currentStep === 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-white mb-6">Select Company</h2>
            
            {companies.length === 0 ? (
              <div className="bg-[#111827] border border-gray-800 rounded-lg p-8 text-center">
                <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <h3 className="text-white font-medium mb-2">No Companies Yet</h3>
                <p className="text-gray-400 text-sm mb-4">
                  You need to add a company before placing an order.
                </p>
                <Link
                  to="/dashboard/companies"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white"
                  style={{ backgroundColor: '#059669' }}
                >
                  Add Company
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                <select
                  value={selectedCompany?.id || ''}
                  onChange={(e) => handleCompanySelect(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#059669] focus:ring-1 focus:ring-[#059669]"
                  data-testid="company-select"
                >
                  <option value="">Select a company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.company_name} ({company.state_of_formation})
                    </option>
                  ))}
                </select>
                
                {selectedCompany && (
                  <div className="bg-[#111827] border border-gray-800 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-[#059669]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{selectedCompany.company_name}</p>
                        <p className="text-gray-400 text-sm">{selectedCompany.state_of_formation}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Amendment sub-type selector */}
                {selectedService?.isAmendment && selectedCompany && (
                  <div className="mt-6">
                    <h3 className="text-white font-medium mb-3">What type of filing do you need?</h3>
                    
                    {ENTITY_TYPE_MAP[selectedCompany.entity_type] === null ? (
                      <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                        <p className="text-yellow-400 text-sm">
                          Please contact us for sole proprietorship filings.
                        </p>
                      </div>
                    ) : (
                      <select
                        value={amendmentSubType}
                        onChange={(e) => setAmendmentSubType(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#059669] focus:ring-1 focus:ring-[#059669]"
                        data-testid="amendment-type-select"
                      >
                        <option value="">Select amendment type</option>
                        {AMENDMENT_SUBTYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {fetchingAmendmentFee && (
                      <div className="flex items-center gap-2 text-gray-400 text-sm mt-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading state fee...
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between mt-8">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 transition-colors"
                data-testid="step2-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleNext}
                disabled={!selectedCompany || fetchingPrice || (selectedService?.isAmendment && !amendmentSubType && ENTITY_TYPE_MAP[selectedCompany?.entity_type] !== null)}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#059669' }}
                data-testid="step2-next-btn"
              >
                {fetchingPrice ? 'Loading...' : (showDetailsStep ? 'Continue' : 'Proceed to Payment')}
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Price Summary Sidebar */}
          <div>
            {selectedService && (
              <div className="bg-[#111827] border border-gray-800 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">{selectedService.name}</span>
                    <span className="text-white">${ourFee}</span>
                  </div>
                  
                  {selectedService.isAmendment && amendmentSubType && (
                    <div className="text-gray-500 text-xs">
                      Type: {AMENDMENT_SUBTYPES.find(t => t.value === amendmentSubType)?.label}
                    </div>
                  )}
                  
                  {stateFee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">State Fee</span>
                      <span className="text-white">${stateFee}</span>
                    </div>
                  )}
                  
                  {selectedService.isAmendment && amendmentStateFee === null && amendmentSubType && (
                    <div className="text-gray-500 text-xs">
                      State fee: TBD (we'll confirm before processing)
                    </div>
                  )}
                  
                  {/* Add-ons for formation */}
                  {selectedService.isFormation && selectedAddOns.length > 0 && (
                    <>
                      <div className="border-t border-gray-700 pt-3 mt-3">
                        <p className="text-gray-500 text-xs mb-2">Add-ons:</p>
                        {selectedAddOns.map(addOnId => {
                          const addOn = ADD_ONS.find(a => a.id === addOnId);
                          return addOn ? (
                            <div key={addOnId} className="flex justify-between">
                              <span className="text-gray-400">{addOn.name}</span>
                              <span className="text-white">${addOn.fee}{addOn.recurring || ''}</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </>
                  )}
                  
                  {/* Partner Discount line — only shows if discount_percentage > 0 */}
                  {discountPercentage > 0 && discountAmount() > 0 && (
                    <div className="flex justify-between pt-2" data-testid="partner-discount-line-sidebar">
                      <span className="text-[#059669]">Partner Discount ({discountPercentage}%)</span>
                      <span className="text-[#059669]">−${discountAmount().toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-gray-700 pt-3 mt-3 flex justify-between">
                    <span className="text-white font-semibold">Total</span>
                    <span className="text-[#059669] font-bold">${calculateTotalWithAddOns()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Details (only if showDetailsStep) */}
      {currentStep === 3 && showDetailsStep && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <InformationStep
              informationFields={product?.information_fields || []}
              documentRequirements={(product?.document_requirements?.length ? product.document_requirements : SERVICE_DOCUMENT_REQUIREMENTS[selectedService?.id]) || []}
              collectedInformation={collectedInformation}
              setCollectedInformation={setCollectedInformation}
              uploadedDocuments={uploadedDocuments}
              setUploadedDocuments={setUploadedDocuments}
              companyId={selectedCompany?.id}
              onBack={handleBack}
              onContinue={handleNext}
              isValid={isDetailsStepValid()}
            />
          </div>

          {/* Price Summary Sidebar */}
          <div>
            <OrderSummary
              service={selectedService}
              company={selectedCompany}
              ourFee={ourFee}
              stateFee={stateFee}
              total={total}
              discountPercentage={discountPercentage}
            />
          </div>
        </div>
      )}

      {/* Step 3 (no details) or Step 4 (with details): Payment */}
      {((currentStep === 3 && !showDetailsStep) || (currentStep === 4 && showDetailsStep)) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-white mb-6">Choose Payment Method</h2>
            
            <div className="space-y-4">
              {/* Card Payment Option */}
              <button
                onClick={() => setPaymentMethod('card')}
                className={`w-full text-left p-5 rounded-lg border-2 transition-all ${
                  paymentMethod === 'card'
                    ? 'border-[#059669] bg-[#059669]/10'
                    : 'border-gray-800 bg-[#111827] hover:border-gray-600'
                }`}
                data-testid="payment-card-option"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-[#059669]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Pay with Card</h3>
                    <p className="text-gray-400 text-sm">Secure payment via Stripe</p>
                  </div>
                </div>
              </button>

              {/* Bank Transfer Option */}
              <button
                onClick={() => setPaymentMethod('bank')}
                className={`w-full text-left p-5 rounded-lg border-2 transition-all ${
                  paymentMethod === 'bank'
                    ? 'border-[#059669] bg-[#059669]/10'
                    : 'border-gray-800 bg-[#111827] hover:border-gray-600'
                }`}
                data-testid="payment-bank-option"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-[#059669]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Pay by Bank Transfer</h3>
                    <p className="text-gray-400 text-sm">Manual transfer — verified by our team</p>
                  </div>
                </div>
              </button>
            </div>

            <div className="flex justify-between mt-8">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 transition-colors"
                data-testid="step-payment-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              
              {paymentMethod === 'card' ? (
                <button
                  onClick={handleStripePayment}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#059669' }}
                  data-testid="pay-card-btn"
                >
                  {loading ? 'Processing...' : `Pay $${calculateTotalWithAddOns()}`}
                  <CreditCard className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleBankTransfer}
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#059669' }}
                  data-testid="pay-bank-btn"
                >
                  {loading ? 'Processing...' : 'Place Order'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Detailed Order Summary Sidebar */}
          <div>
            <div className="bg-[#111827] border border-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>
              
              {/* Main Service */}
              <div className="pb-4 border-b border-gray-700">
                <div className="flex justify-between mb-1">
                  <span className="text-white font-medium">{selectedService?.name}</span>
                  <span className="text-white">${ourFee}</span>
                </div>
                <p className="text-gray-500 text-xs">{selectedService?.description}</p>
                
                {selectedCompany && (
                  <p className="text-gray-400 text-xs mt-2">
                    Company: {selectedCompany.company_name}
                  </p>
                )}
                
                {selectedService?.isAmendment && amendmentSubType && (
                  <p className="text-gray-400 text-xs mt-1">
                    Type: {AMENDMENT_SUBTYPES.find(t => t.value === amendmentSubType)?.label}
                  </p>
                )}
              </div>
              
              {/* State Fee */}
              {(stateFee > 0 || (selectedService?.hasStateFee && selectedService?.isAmendment)) && (
                <div className="py-3 border-b border-gray-700">
                  <div className="flex justify-between">
                    <span className="text-gray-400">State Fee ({selectedCompany?.state_of_formation})</span>
                    <span className="text-white">
                      {stateFee > 0 ? `$${stateFee}` : 'TBD'}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Add-ons */}
              {selectedService?.isFormation && selectedAddOns.length > 0 && (
                <div className="py-3 border-b border-gray-700">
                  <p className="text-gray-500 text-xs uppercase tracking-wide mb-2">Add-ons</p>
                  {selectedAddOns.map(addOnId => {
                    const addOn = ADD_ONS.find(a => a.id === addOnId);
                    return addOn ? (
                      <div key={addOnId} className="mb-2">
                        <div className="flex justify-between">
                          <span className="text-white text-sm">{addOn.name}</span>
                          <span className="text-white text-sm">${addOn.fee}{addOn.recurring || ''}</span>
                        </div>
                        <p className="text-gray-500 text-xs">{addOn.description}</p>
                      </div>
                    ) : null;
                  })}
                </div>
              )}

              {/* Partner Discount line — only shows if discount_percentage > 0 */}
              {discountPercentage > 0 && discountAmount() > 0 && (
                <div className="py-3 border-b border-gray-700" data-testid="partner-discount-line-checkout">
                  <div className="flex justify-between">
                    <span className="text-[#059669] font-medium">Partner Discount ({discountPercentage}%)</span>
                    <span className="text-[#059669] font-medium">−${discountAmount().toFixed(2)}</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">Applied to service fee only</p>
                </div>
              )}

              {/* Total */}
              <div className="pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-white font-semibold">Total Amount</span>
                  <span className="text-[#059669] text-xl font-bold" data-testid="checkout-total">
                    ${calculateTotalWithAddOns()}
                  </span>
                </div>
                {selectedService?.isFormation && selectedAddOns.length > 0 && (
                  <p className="text-gray-500 text-xs mt-2">
                    {1 + selectedAddOns.length} order{selectedAddOns.length > 0 ? 's' : ''} will be created
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default NewOrderPage;
